import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Grid,
  Divider
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { getRates, postRates } from '../api/rates';

const SetRatesModal = ({ open, onClose, employee, onSave }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Initialize rates state with default values from the image
  const [rates, setRates] = useState({
    daily: 570,
    holiday: 1,
    holidayot: 2.6,
    holidayrestday: 0.6,
    holidayrestdayot: 3.38,
    nightshift: 0.1,
    overtime: 1.25,
    // sssRate: 532.5,
    pagibig: 100,
    philhealth: 297.35,
    restday: 0.3,
    restdayot: 1.69,
    specialholiday: 0.3,
    specialholidayot: 0.3,
    specialholidayrestday: 1.69,
    specialholidayrestdayot: 1.95,
    allowance: 0,
  });

  // Reset rates when modal opens with new employee
  useEffect(() => {
    if (open && employee) {
      setRates(employee);
    }
  }, [open, employee]);

  const handleRateChange = (field, value) => {
    const parsed = parseFloat(value);
    if (parsed !== '' && parsed < 0) {
      return;
    }
    setRates(prev => ({
      ...prev,
      [field]: isNaN(parsed) ? 0 : parsed   // ✅ 0 stays 0
    }));
  };

  const handleSave = () => {
    // In a real app, you would save the rates to the API
    enqueueSnackbar(`Rates saved for ${employee?.name}`, { variant: 'success' });
    if (onSave) {
      onSave(rates);
    }
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  if (!employee) return null;

  const rateFields = [
    { key: 'daily', label: 'Daily Rate', value: rates.daily },
    { key: 'holiday', label: 'Holiday Rate', value: rates.holiday },
    { key: 'holidayot', label: 'Holiday OT Rate', value: rates.holidayot },
    { key: 'holidayrestday', label: 'Holiday Rest Day Rate', value: rates.holidayrestday },
    { key: 'holidayrestdayot', label: 'Holiday Rest Day OT Rate', value: rates.holidayrestdayot },
    { key: 'nightshift', label: 'Nightshift Rate', value: rates.nightshift },
    { key: 'nightshiftot', label: 'Nightshift OT Rate', value: rates.nightshiftot },
    { key: 'holidaynightshift', label: 'Holiday Nightshift Rate', value: rates.holidaynightshift },
    { key: 'holidaynightshiftot', label: 'Holiday Nightshift OT Rate', value: rates.holidaynightshiftot },
    { key: 'specialnightshift', label: 'Special Holiday Nightshift Rate', value: rates.specialnightshift },
    { key: 'specialnightshiftot', label: 'Special Holiday ND OT Rate', value: rates.specialnightshift },
    { key: 'overtime', label: 'Overtime Rate', value: rates.overtime },
    // { key: 'sssRate', label: 'SSS Rate', value: rates.sssRate },
    { key: 'pagibig', label: 'Pag-ibig Rate', value: rates.pagibig },
    { key: 'philhealth', label: 'Philhealth Rate', value: rates.philhealth },
    { key: 'restday', label: 'Rest Day Duty Rate', value: rates.restday },
    { key: 'restdayot', label: 'Rest Day OT Rate', value: rates.restdayot },
    { key: 'specialholiday', label: 'Special Holiday Rate', value: rates.specialholiday },
    { key: 'specialholidayot', label: 'Special Holiday Rate', value: rates.specialholidayot },
    { key: 'specialholidayrestday', label: 'Special Holiday RD Rate', value: rates.specialholidayrestday },
    { key: 'specialholidayrestdayot', label: 'Special Holiday RD OT Rate', value: rates.specialholidayrestdayot },
    { key: 'allowance', label: 'Allowance', value: rates.allowance }
  ];
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid #e5e7eb'
      }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600, color: '#374151' }}>
          {employee.name} - RATES
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: '#6b7280',
            '&:hover': { bgcolor: '#f3f4f6' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          {rateFields.map((field, index) => (
            <Box key={field.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography 
                variant="body2" 
                sx={{ 
                  minWidth: '200px', 
                  fontWeight: 500, 
                  color: '#374151',
                  fontSize: '0.875rem'
                }}
              >
                {field.label}:
              </Typography>
              <TextField
                value={field.value}
                onChange={(e) => handleRateChange(field.key, e.target.value)}
                type="number"
                size="small"
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#12C1DC' },
                    '&.Mui-focused fieldset': { borderColor: '#12C1DC' }
                  }
                }}
                inputProps={{
                  step: 'any',
                  min: 0,
                  onWheel: (e) => e.target.blur()
                }}
              />
            </Box>
          ))}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 3, gap: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: '#e5e7eb',
            color: '#6b7280',
            textTransform: 'none',
            px: 3,
            py: 1,
            '&:hover': {
              borderColor: '#12C1DC',
              color: '#12C1DC'
            }
          }}
        >
          Close
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            bgcolor: '#12C1DC',
            '&:hover': { bgcolor: '#0FABDB' },
            textTransform: 'none',
            px: 3,
            py: 1
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetRatesModal;
